<template>
  <card-dialog
    v-model="modalVisible"
    max-width="800px"
    :title="$t('button.filters')"
  >
    <v-card-text class="pa-6">
      <v-row align="center">
        <v-col cols="4">
          {{ $t("text_input.teams.title") }}
        </v-col>
        <v-col cols="8">
          <v-autocomplete
            v-model="filters.teamIds"
            outlined
            :items="teamSelectItems"
            item-value="id"
            item-text="name"
            dense
            multiple
            hide-details
            :placeholder="$t('dropdown.all.placeholder')"
          />
        </v-col>
        <v-col
          v-if="playersByTeam.length"
          cols="4"
        >
          {{ $t("dropdown.players.title") }}
        </v-col>
        <v-col
          v-if="playersByTeam.length"
          cols="8"
        >
          <v-autocomplete
            key="advancedAnalysisPlayersFilter"
            v-model="filters.playerIds"
            :items="playersByTeam"
            item-value="id"
            :item-text="(player) => player.first_name + ' ' + player.last_name"
            :loading="players.loading"
            outlined
            dense
            clearable
            hide-details
            multiple
            :placeholder="$t('dropdown.all.placeholder')"
          >
            <template v-slot:item="{ item }">
              <v-list-item-content>
                <v-list-item-title>
                  {{ item.last_name }}, {{ item.first_name }}
                </v-list-item-title>
                <v-list-item-subtitle>
                  <span v-if="item.team">{{ item.team.short_name }}</span>
                  <span
                    v-if="item.player_positions && item.player_positions.length > 0"
                  >&middot; {{ item.player_positions.map(p => p.short_name).join(', ') }}</span>
                </v-list-item-subtitle>
              </v-list-item-content>
            </template>
            <template v-slot:selection="{ item, index }">
              <span
                v-if="index > 0 && index < 3"
                class="mr-1"
              >, </span>
              <span v-if="index >= 0 && index < 3">{{ item.first_name }} {{ item.last_name }}</span>
              <span
                v-else-if="index === 3"
                class="grey--text text-caption ml-1"
              >
                {{ $t('event.and_others') }}
              </span>
            </template>
          </v-autocomplete>
        </v-col>
        <v-col cols="12">
          <v-divider />
        </v-col>
        <v-col cols="4">
          {{ $t("picker.date.date_range.title") }}
        </v-col>
        <v-col cols="8">
          <v-menu
            v-model="filterDateAdvancedActive"
            :close-on-content-click="false"
          >
            <template v-slot:activator="{ on }">
              <v-text-field
                :value="filters.dateRange.length === 1 ? $dayjs(filters.dateRange[0]).format('ll - [today]') : (filters.dateRange.length === 2 ? filters.dateRange.map(d => $dayjs(d).format('ll')).join(' - ') : undefined)"
                class="custom-height-text-field"
                outlined
                clearable
                dense
                hide-details
                @click:clear="filters.dateRange = []"
                v-on="on"
              />
            </template>
            <v-date-picker
              v-model="filters.dateRange"
              :first-day-of-week="1"
              no-title
              range
              :min="filters.dateRange.length === 1 ? filters.dateRange[0] : undefined"
              :max="$dayjs().format('YYYY-MM-DD')"
              @change="$event.length === 2 ? filterDateAdvancedActive = false : undefined"
            />
          </v-menu>
        </v-col>
        <v-col cols="4">
          {{ $t('dropdown.event_type.title') }}
        </v-col>
        <v-col cols="8">
          <v-select
            v-model="filters.eventType"
            :items="mainEventTypesList"
            item-value="key"
            item-text="text"
            hide-details
            dense
            outlined
            clearable
            :placeholder="$t('dropdown.all.placeholder')"
          />
        </v-col>
        <v-col cols="11">
          {{ $t("card_dialog.advanced_performance_filters.official_match") }}
        </v-col>
        <v-col cols="1">
          <v-switch
            v-model="filters.onlyOfficialMatch"
            hide-details
            class="ma-0 pa-0"
          />
        </v-col>
        <v-col cols="4">
          {{ $t("card_dialog.advanced_performance_filters.events") }}
        </v-col>
        <v-col cols="8">
          <v-autocomplete
            key="advancedSelectEvents"
            v-model="filters.eventIds"
            :items="events.data"
            :loading="events.loading"
            outlined
            dense
            hide-details
            item-value="id"
            item-text="title"
            :placeholder="$t('dropdown.all.placeholder')"
            multiple
            clearable
            :menu-props="{ maxWidth: '500px', width: '500px' }"
          >
            <template v-slot:item="{ item }">
              <v-list-item-content>
                <v-list-item-title>
                  {{ item.title }}
                </v-list-item-title>
                <v-list-item-subtitle>
                  {{ $dayjs(item.start_at).format(getDateTimeFormatForUser('dddd, ll HH:mm:ss')) }}
                </v-list-item-subtitle>
              </v-list-item-content>
            </template>
            <template v-slot:selection="{ item, index }">
              <span
                v-if="index > 0 && index < 2"
                class="mr-1"
              >, </span>
              <span v-if="index >= 0 && index < 2">{{ item.title }}</span>
              <span
                v-else-if="index === 2"
                class="grey--text text-caption ml-1"
              >
                {{ $t('event.and_others') }}
              </span>
            </template>
          </v-autocomplete>
        </v-col>
        <v-col cols="4">
          {{ $t('card_dialog.advanced_performance_filters.exercises') }}
        </v-col>
        <v-col cols="8">
          <v-autocomplete
            key="advancedSelectExercise"
            v-model="filters.exerciseIds"
            :items="exercises.data"
            :loading="exercises.loading"
            outlined
            dense
            hide-details
            item-value="id"
            item-text="name"
            multiple
            :placeholder="$t('dropdown.all.placeholder')"
            clearable
            :menu-props="{ maxWidth: '500px', width: '500px' }"
          />
        </v-col>
        <v-col
          v-if="playersByTeam.length"
          cols="11"
        >
          <subtitle>
            {{ $t("card_dialog.advanced_performance_filters.compare") }}
          </subtitle>
        </v-col>
        <v-col
          v-if="playersByTeam.length"
          cols="1"
        >
          <v-switch
            v-model="showCompare"
            hide-details
          />
        </v-col>
        <template v-if="showCompare">
          <v-col cols="4">
            {{ $t("picker.date.date_range.title") }}
          </v-col>
          <v-col cols="8">
            <v-menu
              v-model="filterDateAdvancedComparisonActive"
              :close-on-content-click="false"
            >
              <template v-slot:activator="{ on }">
                <v-text-field
                  :value="comparisonFilters.dateRange.length === 1 ? $dayjs(comparisonFilters.dateRange[0]).format('ll - [today]') : (comparisonFilters.dateRange.length === 2 ? comparisonFilters.dateRange.map(d => $dayjs(d).format('ll')).join(' - ') : undefined)"
                  class="custom-height-text-field"
                  outlined
                  clearable
                  dense
                  hide-details
                  @click:clear="comparisonFilters.dateRange = []"
                  v-on="on"
                />
              </template>
              <v-date-picker
                v-model="comparisonFilters.dateRange"
                :first-day-of-week="1"
                no-title
                range
                :min="comparisonFilters.dateRange.length === 1 ? comparisonFilters.dateRange[0] : undefined"
                :max="$dayjs().format('YYYY-MM-DD')"
                @change="$event.length === 2 ? filterDateAdvancedActive = false : undefined"
              />
            </v-menu>
          </v-col>
          <v-col cols="4">
            {{ $t('dropdown.event_type.title') }}
          </v-col>
          <v-col cols="8">
            <v-select
              v-model="comparisonFilters.eventType"
              :items="mainEventTypesList"
              item-value="key"
              item-text="text"
              hide-details
              dense
              outlined
              clearable
              :placeholder="$t('dropdown.all.placeholder')"
            />
          </v-col>
          <v-col cols="11">
            {{ $t("card_dialog.advanced_performance_filters.official_match") }}
          </v-col>
          <v-col cols="1">
            <v-switch
              v-model="comparisonFilters.onlyOfficialMatch"
              hide-details
              class="ma-0 pa-0"
            />
          </v-col>
          <v-col cols="4">
            {{ $t("card_dialog.advanced_performance_filters.events") }}
          </v-col>
          <v-col cols="8">
            <v-autocomplete
              key="advancedSelectEvents"
              v-model="comparisonFilters.eventIds"
              :items="comparisonEvents.data"
              :loading="comparisonEvents.loading"
              outlined
              dense
              hide-details
              item-value="id"
              item-text="title"
              :placeholder="$t('dropdown.all.placeholder')"
              multiple
              clearable
              :menu-props="{ maxWidth: '500px', width: '500px' }"
            >
              <template v-slot:item="{ item }">
                <v-list-item-content>
                  <v-list-item-title>
                    {{ item.title }}
                  </v-list-item-title>
                  <v-list-item-subtitle>
                    {{ $dayjs(item.start_at).format(getDateTimeFormatForUser('dddd, ll HH:mm:ss')) }}
                  </v-list-item-subtitle>
                </v-list-item-content>
              </template>
              <template v-slot:selection="{ item, index }">
                <span
                  v-if="index > 0 && index < 2"
                  class="mr-1"
                >, </span>
                <span v-if="index >= 0 && index < 2">{{ item.title }}</span>
                <span
                  v-else-if="index === 2"
                  class="grey--text text-caption ml-1"
                >
                  {{ $t('event.and_others') }}
                </span>
              </template>
            </v-autocomplete>
          </v-col>
          <v-col cols="4">
            {{ $t('card_dialog.advanced_performance_filters.exercises') }}
          </v-col>
          <v-col cols="8">
            <v-autocomplete
              key="advancedSelectExercise"
              v-model="comparisonFilters.exerciseIds"
              :items="comparisonExercises.data"
              :loading="comparisonExercises.loading"
              outlined
              dense
              hide-details
              item-value="id"
              item-text="name"
              multiple
              :placeholder="$t('dropdown.all.placeholder')"
              clearable
              :menu-props="{ maxWidth: '500px', width: '500px' }"
            />
          </v-col>
        </template>
      </v-row>
    </v-card-text>
    <template v-slot:actions>
      <v-card-actions class="pa-4 grey-bg">
        <v-spacer />
        <v-btn
          color="primary"
          class="text-none"
          @click="handleClick"
        >
          {{ $t('button.apply') }}
        </v-btn>
      </v-card-actions>
    </template>
  </card-dialog>
</template>

<script>

import CardDialog from '@/components/helpers/CardDialog.vue'
import { EventTypes } from '@/data/models'
import { getDateTimeFormatForUser } from '@/utils/helpers'
import Subtitle from '@/components/helpers/Subtitle.vue'

export default {
  components: { Subtitle, CardDialog },
  props: {
    visible: {
      type: Boolean,
      default: false
    },
    filters: {
      type: Object,
      default: () => ({})
    },
    comparisonFilters: {
      type: Object,
      default: () => ({})
    },
    events: {
      type: Object,
      default: () => ({})
    },
    comparisonEvents: {
      type: Object,
      default: () => ({})
    },
    players: {
      type: Object,
      default: () => ({})
    },
    exercises: {
      type: Object,
      default: () => ({})
    },
    comparisonExercises: {
      type: Object,
      default: () => ({})
    }
  },
  data() {
    return {
      filterDateAdvancedActive: false,
      filterDateAdvancedComparisonActive: false,
      showCompare: false
    }
  },
  computed: {
    modalVisible: {
      get () {
        return this.visible
      },
      set (value) {
        this.$emit('update:visible', value)
      }
    },
    teamSelectItems() {
      return this.$store.getters[this.$storeGetters.APP_TEAMS]
    },
    currentTeam() {
      return this.$store.getters[this.$storeGetters.APP_CURRENT_TEAM]
    },
    playersByTeam() {
      return this.players.data ? this.players.data.filter(p => this.filters.teamIds.indexOf(p.team_id) > -1) : []
    }
  },
  watch: {

  },
  created() {
    this.mainEventTypesList = Object.keys(EventTypes).map(k => {
      return {
        ...EventTypes[k],
        key: k
      }
    }).filter(e => e.is_other === false)
  },
  methods: {
    getDateTimeFormatForUser,
    handleClick() {
      this.modalVisible = false

      if (this.showCompare) {
        this.$emit('handle:comparison', {
          numerator: this.filters,
          denominator: this.comparisonFilters
        })
      } else {
        this.$emit('handle:advanced', this.filters)
      }
    }
  }

}
</script>

<style scoped>

</style>
